import * as React from 'react';
import { classNames, useUid } from '@/utils';

/**
 * The `SelectItem` type extends the built-in `Record` type, which allows for arbitrary key-value pairs.
 */
export interface SelectItem extends Record<string, any> {}

/**
 * A function that takes a `SelectItem` object as input and returns an object containing a `value` and a `label` property.
 * - `value`: the value to be passed as the `value` prop to the select component
 * - `label`: the label to be displayed for the option in the select component
 * - `disabled`: whether the option should be disabled (optional)
 */
type OptionResolver = (option: SelectItem) => {
  value: string;
  label: string;
  disabled?: boolean;
};

function defaultOptionResolver(option: SelectItem | string) {
  if (typeof option === 'string') {
    return { label: option, value: option };
  }

  if ('id' in option && ('name' in option || 'title' in option)) {
    return { label: option.name || option.title, value: option.id };
  }

  return option;
}
export interface SelectProps {
  /** The HTML ID attribute to be applied to the select element (optional). */
  id?: string;
  /** The label to be displayed above the select field (optional). */
  label?: React.ReactNode;
  /** The error message to be displayed below the select field (optional). */
  error?: string;
  /** The help text to be displayed below the select field (optional). */
  helpText?: React.ReactNode;
  /** An array of strings or `SelectItem` objects that represent the options in the select field (required). */
  data: (string | SelectItem)[];
  /** Whether the select field should be disabled (optional). */
  disabled?: boolean;
  /** The default value of the select field (optional). */
  defaultValue?: string;
  /** The element to be displayed to the left of the select field (optional). */
  prefix?: React.ReactNode;
  /** The width of the prefix element (optional). */
  prefixWidth?: number;
  /**
   * A function that is called when the value of the select field changes (optional).
   * This function accepts the new value of the select field as input.
   */
  onChange?(value: string): void;

  /**
   * A function that takes a `SelectItem` object as input and returns an object containing a `value` and a `label` property (optional).
   * This function is used to map the `SelectItem` objects in the `data` array to option objects that are used to render the select field.
   * If this prop is not provided, the `value` and `label` properties of the `SelectItem` objects will be used.
   */
  optionResolver?: OptionResolver;

  /** CSS class names to be applied to the select element (optional). */
  selectClassName?: string;
  /** CSS class names to be applied to the label element (optional). */
  labelClassName?: string;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>((props, ref) => {
  const {
    label,
    data,
    helpText,
    prefix,
    prefixWidth = prefix ? 24 : 12,
    onChange,
    disabled,
    id,
    selectClassName,
    labelClassName,
    error,
    optionResolver = defaultOptionResolver,
    defaultValue,
  } = props;
  const uid = useUid(id);
  const normalizedData = data.map(optionResolver);

  const options = normalizedData.map((item) => (
    <option key={item.value} value={item.value} disabled={item.disabled}>
      {item.label}
    </option>
  ));

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onChange?.(event.currentTarget.value);
  };

  return (
    <label htmlFor={uid} className={classNames({ error }, labelClassName)}>
      <p>{label}</p>
      <div className={classNames({ 'prefixed-input-container': prefix })}>
        {prefix && <span className="input-prefix">{prefix}</span>}
        <select
          id={uid}
          ref={ref}
          className={classNames({ prefixed: prefix }, selectClassName)}
          disabled={disabled}
          defaultValue={defaultValue}
          style={{ paddingLeft: prefixWidth }}
          onChange={handleChange}
        >
          {options}
        </select>
      </div>
      <small>{error || helpText}</small>
    </label>
  );
});

Select.displayName = 'Select';
